import { useRef, useState } from "react";
import { X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BoardBackground } from "@kandoo/shared";
import { api, type BoardDetail } from "../api/client";
import { Modal } from "./Modal";
import {
  COLOR_PRESETS,
  GRADIENT_PRESETS,
  MAX_BG_IMAGE_BYTES,
  PATTERN_PRESETS,
  backgroundStyle,
  patternPreviewStyle,
} from "../util/backgrounds";

interface BoardSettingsProps {
  board: BoardDetail;
  onClose: () => void;
}

/** Owner-only board settings: title, description, and background. */
export function BoardSettings({ board, onClose }: BoardSettingsProps) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["board", board.id] });
    qc.invalidateQueries({ queryKey: ["boards"] });
  };
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.boards.update>[1]) =>
      api.boards.update(board.id, patch),
    onSuccess: invalidate,
  });

  const [title, setTitle] = useState(board.title);
  const [description, setDescription] = useState(board.description ?? "");

  const setBackground = (bg: BoardBackground | null) =>
    update.mutate({ background: bg });

  /** Change the base fill but keep any overlay pattern. */
  const setBase = (base: Omit<BoardBackground, "pattern">) =>
    setBackground({ ...base, pattern: board.background?.pattern });

  /** Change the overlay pattern on the current base (no-op without a base). */
  const setPattern = (pattern: string | undefined) => {
    if (!board.background) return;
    setBackground({ ...board.background, pattern });
  };

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/(png|svg|jpe?g)/i.test(file.type)) {
      setError("Please choose a PNG, JPEG, or SVG image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = String(reader.result);
      if (dataUri.length > MAX_BG_IMAGE_BYTES) {
        setError("That image is too large (max ~1.5 MB). Try a smaller file.");
        return;
      }
      setBase({ type: "image", value: dataUri });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  const bg = board.background;

  return (
    <Modal onClose={onClose} ariaLabel="Board settings">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Board settings</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        <label className="block text-sm">
          <span className="text-slate-600">Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              const t = title.trim();
              if (t && t !== board.title) update.mutate({ title: t });
              else setTitle(board.title);
            }}
            className="mt-1 w-full rounded border border-slate-300 bg-[var(--surface)] px-3 py-2 text-sm"
          />
        </label>

        <label className="mt-3 block text-sm">
          <span className="text-slate-600">Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => {
              const d = description.trim();
              if (d !== (board.description ?? ""))
                update.mutate({ description: d || null });
            }}
            rows={2}
            className="mt-1 w-full resize-none rounded border border-slate-300 bg-[var(--surface)] px-3 py-2 text-sm"
          />
        </label>

        {/* Background */}
        <div className="mt-4">
          <div className="mb-2 text-sm font-medium text-slate-700">Background</div>

          <div className="mb-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setBackground(null)}
              title="No background"
              className={`flex h-9 w-9 items-center justify-center rounded border border-slate-300 text-slate-400 ${
                !bg ? "ring-2 ring-slate-900 ring-offset-1" : ""
              }`}
            >
              <X size={16} />
            </button>
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setBase({ type: "color", value: c })}
                title={c}
                style={{ backgroundColor: c }}
                className={`h-9 w-9 rounded ${
                  bg?.type === "color" && bg.value === c
                    ? "ring-2 ring-slate-900 ring-offset-1"
                    : ""
                }`}
              />
            ))}
          </div>

          <div className="mb-2 flex flex-wrap gap-2">
            {GRADIENT_PRESETS.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setBase({ type: "gradient", value: g.css })}
                title={g.id}
                style={{ backgroundImage: g.css }}
                className={`h-9 w-14 rounded ${
                  bg?.type === "gradient" && bg.value === g.css
                    ? "ring-2 ring-slate-900 ring-offset-1"
                    : ""
                }`}
              />
            ))}
          </div>

          {/* Overlay pattern (rides on top of the color/gradient/image) */}
          <div className="mb-1 mt-3 text-sm font-medium text-slate-700">
            Pattern overlay
          </div>
          {!bg && (
            <p className="mb-2 text-xs text-slate-500">
              Pick a background first — patterns lay over it.
            </p>
          )}
          <div className="mb-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPattern(undefined)}
              disabled={!bg}
              title="No pattern"
              className={`flex h-9 w-14 items-center justify-center rounded border border-slate-300 text-slate-400 disabled:opacity-40 ${
                bg && !bg.pattern ? "ring-2 ring-slate-900 ring-offset-1" : ""
              }`}
            >
              <X size={16} />
            </button>
            {PATTERN_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPattern(p.id)}
                disabled={!bg}
                title={p.label}
                style={patternPreviewStyle(p.id)}
                className={`h-9 w-14 rounded disabled:opacity-40 ${
                  bg?.pattern === p.id ? "ring-2 ring-slate-900 ring-offset-1" : ""
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded bg-slate-100 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
            >
              Upload PNG / JPEG / SVG…
            </button>
            {bg?.type === "image" && (
              <div
                className="h-9 w-14 rounded border border-slate-300"
                style={backgroundStyle(bg)}
              />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={onFile}
              className="hidden"
            />
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        {/* Cards */}
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="mb-2 text-sm font-medium text-slate-700">Cards</div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={board.cardPreviews ?? false}
              onChange={() =>
                update.mutate({ cardPreviews: !board.cardPreviews })
              }
              className="rounded border-slate-300"
            />
            <span>Show image previews on cards</span>
          </label>
        </div>

        {/* Export */}
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="mb-2 text-sm font-medium text-slate-700">Export</div>
          <a
            href={`/api/boards/${board.id}/export`}
            download
            className="inline-block rounded bg-slate-100 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200"
          >
            Export board (JSON)
          </a>
        </div>
      </div>
    </Modal>
  );
}
